import { CLUB_ORG_TYPES, type ClubOrgType } from '@kk/domain';
import { Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { errorCode, useErrorMessage, useSubmitClubApplication } from '@/data/hooks';
import { SignInShell } from '@/screens/SignInShell';

const c = deck.o18;
type Field = keyof typeof c.fields;
const TEXT_FIELDS: Field[] = [
  'clubName',
  'contactName',
  'email',
  'phone',
  'cityState',
  'kidsCount',
  'kidsAges',
  'heardFrom',
  'website',
];

/**
 * **O18, clubs.kiddiekredit.com/apply** (Evan, 2026-09-27): a club asks to join, in O00's frame, with no account. The
 * village code from a family's "Invite your club" link rides along as `ref`. A hidden field a person never sees catches
 * form-filling bots; the API also limits how many arrive from one place in an hour. No outside captcha.
 */
export function ApplyScreen() {
  const submit = useSubmitClubApplication();
  const errorMessage = useErrorMessage();
  const [values, setValues] = useState<Record<Field, string>>(
    () => Object.fromEntries(TEXT_FIELDS.map((f) => [f, ''])) as Record<Field, string>,
  );
  const [orgType, setOrgType] = useState<ClubOrgType | null>(null);
  const [homepage, setHomepage] = useState('');
  const [sentBy, setSentBy] = useState<string | null>(null);
  const [params] = useSearchParams();
  const ref = params.get('ref')?.trim() || undefined;
  const set = (f: Field) => (v: string) => setValues((old) => ({ ...old, [f]: v }));
  const ready =
    !!orgType &&
    TEXT_FIELDS.filter((f) => f !== 'website').every((f) => values[f].trim().length > 0) &&
    !submit.isPending;

  const send = () => {
    if (!ready || !orgType) return;
    const name = values.contactName.trim().split(/\s+/)[0] ?? values.contactName.trim();
    submit.mutate(
      {
        clubName: values.clubName,
        contactName: values.contactName,
        email: values.email,
        phone: values.phone,
        cityState: values.cityState,
        kidsCount: values.kidsCount,
        kidsAges: values.kidsAges,
        orgType,
        heardFrom: values.heardFrom,
        ...(values.website.trim() ? { website: values.website } : {}),
        ...(ref ? { ref } : {}),
        ...(homepage ? { homepage } : {}),
      },
      { onSuccess: () => setSentBy(name) },
    );
  };

  if (sentBy)
    return (
      <SignInShell>
        <Title2 tone="ink">{c.headline}</Title2>
        <Status testID="apply-thanks">{c.thanks(sentBy)}</Status>
      </SignInShell>
    );

  const error = submit.error
    ? errorCode(submit.error) === 'RATE_LIMITED'
      ? c.tooMany
      : errorMessage(submit.error)
    : null;

  return (
    <SignInShell>
      <form
        className="stack"
        data-testid="apply-form"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <Title2 tone="ink">{c.headline}</Title2>
        <Text variant="bodySm" tone="muted">
          {c.intro}
        </Text>
        {/* the 23 Oct audit, T2-14: the price, before a club applies rather than after it is approved */}
        <Text variant="bodySm" tone="ink" testID="apply-price">
          {c.price}
        </Text>
        {TEXT_FIELDS.slice(0, 7).map((f) => (
          <TextField
            key={f}
            label={c.fields[f]}
            value={values[f]}
            onChangeText={set(f)}
            testID={`apply-${f}`}
          />
        ))}
        <div className="stack">
          <span className="field-label">{c.fields.orgType}</span>
          <div className="actions" role="radiogroup" aria-label={c.fields.orgType}>
            {CLUB_ORG_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                className={`chip${orgType === t ? ' on' : ''}`}
                aria-checked={orgType === t}
                onClick={() => setOrgType(t)}
                data-testid={`apply-orgType-${t}`}
              >
                {c.orgTypes[t]}
              </button>
            ))}
          </div>
        </div>
        {(['heardFrom', 'website'] as const).map((f) => (
          <TextField
            key={f}
            // the one field a club may leave empty says so (T3-11)
            label={f === 'website' ? `${c.fields[f]} ${c.optional}` : c.fields[f]}
            value={values[f]}
            onChangeText={set(f)}
            testID={`apply-${f}`}
          />
        ))}
        {/* the hidden field: off-screen, out of the tab order, never announced — only a bot fills it in */}
        <input
          className="apply-trap"
          name="homepage"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={homepage}
          onChange={(e) => setHomepage(e.target.value)}
          data-testid="apply-homepage"
        />
        {error ? <Status testID="apply-error">{error}</Status> : null}
        <WasabiButton
          label={c.send}
          disabled={!ready}
          loading={submit.isPending}
          onPress={send}
          testID="apply-send"
        />
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </SignInShell>
  );
}
